import * as React from 'react';

import { useTPAComponentsContext } from '../TPAComponentsConfig';
import { st, classes } from './Pagination.st.css';
import { ButtonType, PaginationMode, PaginationProps } from './types';

import { DataHooks } from './constants';
import { filterDataProps } from '../internal/WixUiCoreComponents/utils/FilterDataProps';
import { NavButton, NavButtonProps } from './components/NavButton';
import { PageInput } from './components/PageInput';
import { PageCompact } from './components/PageCompact';
import { PageStrip } from './components/PageStrip';

export const Pagination: React.FC<PaginationProps> = (props) => {
  const { mobile, rtl } = useTPAComponentsContext();
  const {
    currentPage = 1,
    showFirstLastNavButtons = props.totalPages > (props.maxPagesToShow || 5),
    showFirstPage = false,
    showLastPage = false,
    showNextLabel = true,
    showPreviousLabel = true,
    responsive = false,
    paginationMode = mobile ? 'compact' : 'pages',
    showInputModeTotalPages = false,
    disabled = false,
    gapLabel = '...',
    slashLabel = '/',
    maxPagesToShow = 5,
    inputAriaLabel,
    id,
    totalPages,
    pageUrl,
    onChange,
    onClick,
    onDoubleClick,
    onMouseEnter,
    onMouseLeave,
    className,
    style: inlineStyle,
    width,
  } = props;
  const navRef = React.useRef(null);
  const [pageInputValue, setPageInputValue] = React.useState(`${currentPage}`);
  const [pageInputHasError, setPageInputHasError] = React.useState(false);

  const getMaxPagesToShow = (): number => {
    if (maxPagesToShow) {
      return maxPagesToShow;
    }
    if (responsive) {
      return 20;
    }
    return 7;
  };

  const handlePageClick = (
    event: React.MouseEvent<HTMLAnchorElement>,
    page: number,
  ) => {
    onChange({ event, page });
  };

  const renderPages = () => {
    switch (paginationMode) {
      case PaginationMode.Input:
        const handlePageInputKeyDown = (
          event: React.KeyboardEvent<HTMLInputElement>,
        ) => {
          if (event.key === 'Enter') {
            const page = Number(pageInputValue);
            if (page !== currentPage) {
              if (1 <= page && page <= totalPages) {
                onChange({ event, page });
              } else {
                setPageInputHasError(true);
              }
            }
          }
        };
        const handlePageInputChange = (
          event: React.ChangeEvent<HTMLInputElement>,
        ) => {
          setPageInputHasError(false);
          setPageInputValue(event.target.value);
        };
        const handlePageInputBlur = () => {
          setPageInputHasError(false);
          setPageInputValue(String(currentPage));
        };
        return (
          <PageInput
            totalPages={totalPages}
            pageInputValue={pageInputValue}
            disabled={disabled}
            inputAriaLabel={inputAriaLabel}
            handlePageInputBlur={handlePageInputBlur}
            handlePageInputChange={handlePageInputChange}
            handlePageInputKeyDown={handlePageInputKeyDown}
            showInputModeTotalPages={showInputModeTotalPages}
            slashLabel={slashLabel}
          />
        );
      case PaginationMode.Compact:
        return (
          <PageCompact
            currentPage={currentPage}
            slashLabel={slashLabel}
            totalPages={totalPages}
          />
        );
      case PaginationMode.Pages:
      default:
        return (
          <PageStrip
            id={id}
            totalPages={totalPages}
            currentPage={currentPage}
            maxPagesToShow={getMaxPagesToShow()}
            showFirstPage={showFirstPage}
            showLastPage={showLastPage}
            responsive={responsive}
            pageUrl={pageUrl}
            gapLabel={gapLabel}
            handlePageClick={handlePageClick}
            disabled={disabled}
          />
        );
    }
  };

  const renderEmptyButton = (type: ButtonType) => {
    const btnClass = {
      [ButtonType.Prev]: [classes.emptyButtonPrevious],
      [ButtonType.Next]: [classes.emptyButtonNext],
    }[type] as [string];

    return (
      <div
        data-hook={DataHooks[type]}
        className={st(classes.emptyButton, ...btnClass)}
      />
    );
  };

  const navButtonProps: NavButtonProps = {
    disabled,
    currentPage,
    totalPages,
    previousLabel: props.previousLabel,
    nextLabel: props.nextLabel,
    firstLabel: props.firstLabel,
    lastLabel: props.lastLabel,
    pageUrl,
    handlePageClick,
    id,
    type: undefined,
  };

  return (
    <nav
      ref={navRef}
      aria-label="Pagination"
      aria-disabled={disabled}
      dir={rtl ? 'rtl' : null}
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      style={inlineStyle || { width }}
      className={st(
        classes.root,
        { disabled, error: pageInputHasError, mobile, rtl },
        className,
      )}
      {...filterDataProps({
        ...props,
        'data-mobile': mobile || null,
        'data-error': pageInputHasError || null,
      })}
    >
      {showFirstLastNavButtons && (
        <NavButton {...navButtonProps} type={ButtonType.First} />
      )}
      {showPreviousLabel ? (
        <NavButton {...navButtonProps} type={ButtonType.Prev} />
      ) : (
        renderEmptyButton(ButtonType.Prev)
      )}
      {renderPages()}
      {showNextLabel ? (
        <NavButton {...navButtonProps} type={ButtonType.Next} />
      ) : (
        renderEmptyButton(ButtonType.Next)
      )}
      {showFirstLastNavButtons && (
        <NavButton {...navButtonProps} type={ButtonType.Last} />
      )}
    </nav>
  );
};

Pagination.displayName = 'Pagination';
